import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, AlertCircle, ShieldCheck, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "pm_registration_draft";
const EMPTY = { name: "", email: "", phone: "", location: "", preferred_channel: "whatsapp", programme_confirmed: false, privacy_consent: false };

const Shell = ({ children, testid }) => (
  <div className="min-h-screen bg-[#FAFAF9]" data-testid={testid}>
    <header className="border-b border-stone-200 bg-white">
      <div className="max-w-xl mx-auto px-5 h-16 flex items-center justify-between">
        <img src="/platemind-logo-new.png" alt="PlateMind" className="h-8 w-auto" />
        <span className="flex items-center gap-1.5 text-xs text-stone-500"><Lock className="w-3.5 h-3.5" /> Secure registration</span>
      </div>
    </header>
    <main className="max-w-xl mx-auto px-5 py-10">{children}</main>
  </div>
);

const Field = ({ label, error, hint, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-semibold text-stone-800">{label}</label>
    {children}
    {hint && !error && <p className="text-xs text-stone-400">{hint}</p>}
    {error && <p className="text-xs text-red-600" data-testid="field-error">{error}</p>}
  </div>
);

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your full name.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
  const ph = f.phone.replace(/[\s\-()]/g, "");
  if (!/^\+[1-9]\d{7,14}$/.test(ph)) e.phone = "Please include your country code, for example +234 801 234 5678.";
  if (!f.programme_confirmed) e.programme_confirmed = "Please confirm the programme you are purchasing.";
  if (!f.privacy_consent) e.privacy_consent = "Please accept the privacy notice and terms to continue.";
  return e;
};

const PaymentReturn = ({ registration, reference }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    const url = reference ? `${API}/programme/verify-payment?reference=${encodeURIComponent(reference)}` : `${API}/programme/registrations/${registration}`;
    fetch(url).then(async r => {
      const d = await r.json();
      const status = d.status === "paid" ? "successful" : d.status;
      setState({ status: r.ok ? status : "failed", message: d.message || d.detail, redirect: d.redirect_to });
      if (r.ok && status === "successful" && d.redirect_to) setTimeout(() => navigate(d.redirect_to), 2500);
    }).catch(() => setState({ status: "pending", message: "We couldn't confirm your payment yet. Please refresh in a moment." }));
  }, [registration, reference, navigate]);
  const retry = async () => {
    setRetrying(true);
    try {
      const r = await fetch(`${API}/programme/registrations/${registration}/retry`, { method: "POST" });
      const d = await r.json();
      if (d.already_paid) navigate(d.redirect_to);
      else if (r.ok && d.authorization_url) window.location.href = d.authorization_url;
      else { setState(s => ({ ...s, message: d.detail || "Please try again." })); setRetrying(false); }
    } catch { setRetrying(false); }
  };
  if (state.loading) return <Shell testid="registration-verifying"><Card className="rounded-2xl p-10 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF7A00] mx-auto" /><p className="text-sm text-stone-500 mt-3">Confirming your payment with Paystack...</p></Card></Shell>;
  if (state.status === "successful") return (
    <Shell testid="registration-paid">
      <Card className="rounded-2xl p-10 text-center space-y-4">
        <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
        <h1 className="text-2xl font-bold text-stone-900">Payment confirmed</h1>
        <p className="text-sm text-stone-500">You're enrolled in the 12-week programme. Next, choose a time for your initial consultation. Your appointment is confirmed only once you've booked a time.</p>
        <Button onClick={() => navigate(state.redirect)} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-full px-8 h-12 font-semibold" data-testid="go-to-booking-btn">Book my consultation</Button>
      </Card>
    </Shell>
  );
  return (
    <Shell testid="registration-not-paid">
      <Card className="rounded-2xl p-10 text-center space-y-4">
        <AlertCircle className="w-14 h-14 text-amber-500 mx-auto" />
        <h1 className="text-2xl font-bold text-stone-900" data-testid="payment-status-title">{state.status === "failed" ? "Payment not successful" : "Payment not completed"}</h1>
        <p className="text-sm text-stone-500">{state.message || "Your registration is saved. No payment has been taken."}</p>
        <Button onClick={retry} disabled={retrying} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-full px-8 h-12 font-semibold" data-testid="retry-payment-btn">
          {retrying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Try payment again
        </Button>
        <p className="text-xs text-stone-400">Need help? WhatsApp +234 706 203 9281 or email hello@platemind.app</p>
      </Card>
    </Shell>
  );
};

export default function ProgrammeRegister() {
  const [params] = useSearchParams();
  const registration = params.get("registration");
  const reference = params.get("reference") || params.get("trxref");
  const invite = params.get("ref");
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(() => { try { return { ...EMPTY, ...JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}") }; } catch { return EMPTY; } });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [inviteName, setInviteName] = useState("");

  useEffect(() => { fetch(`${API}/programme/info`).then(r => r.json()).then(setInfo).catch(() => {}); }, []);
  useEffect(() => { if (invite) fetch(`${API}/programme/invite/${encodeURIComponent(invite)}`).then(r => r.ok ? r.json() : null).then(d => d && setInviteName(d.first_name)).catch(() => {}); }, [invite]);
  useEffect(() => { const { privacy_consent, programme_confirmed, ...keep } = form; sessionStorage.setItem(DRAFT_KEY, JSON.stringify(keep)); }, [form]);

  if (registration) return <PaymentReturn registration={registration} reference={reference} />;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const price = info ? `${info.currency} ${Number(info.price).toLocaleString()}` : "";

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/programme/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, invite_token: invite || null }) });
      const d = await r.json();
      if (r.ok && d.authorization_url) { sessionStorage.removeItem(DRAFT_KEY); window.location.href = d.authorization_url; return; }
      if (Array.isArray(d.detail)) {
        const fe = {};
        d.detail.forEach(x => { const k = x.loc?.[x.loc.length - 1]; if (k) fe[k] = (x.msg || "").replace(/^Value error, /, ""); });
        setErrors(fe);
      } else setServerError(d.detail || "Something went wrong. Please try again.");
    } catch { setServerError("Connection problem. Your details are kept; please try again."); }
    setSubmitting(false);
  };

  return (
    <Shell testid="programme-register-page">
      <p className="text-xs font-bold text-[#FF7A00] uppercase tracking-widest">Programme registration</p>
      <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mt-2 leading-tight">{inviteName ? `Welcome, ${inviteName}.` : "Join the PlateMind programme."}</h1>
      <p className="text-stone-500 text-sm mt-3">Complete your details below, then pay securely with Paystack. It takes about 2 minutes. Health questions come later, before your first consultation.</p>

      <form onSubmit={submit} noValidate className="mt-8 space-y-5" data-testid="registration-form">
        <Card className="rounded-2xl border-stone-200 p-6 space-y-5">
          <Field label="Full name" error={errors.name}><Input value={form.name} onChange={set("name")} autoComplete="name" className="h-11 rounded-xl" data-testid="reg-name" /></Field>
          <Field label="Email address" error={errors.email}><Input type="email" value={form.email} onChange={set("email")} autoComplete="email" className="h-11 rounded-xl" data-testid="reg-email" /></Field>
          <Field label="Phone number" error={errors.phone} hint="Include your country code, for example +234 801 234 5678"><Input type="tel" value={form.phone} onChange={set("phone")} placeholder="+234" autoComplete="tel" className="h-11 rounded-xl" data-testid="reg-phone" /></Field>
          <Field label="City and country (optional)" error={errors.location}><Input value={form.location} onChange={set("location")} placeholder="Lagos, Nigeria" className="h-11 rounded-xl" data-testid="reg-location" /></Field>
          <Field label="How should we contact you?" error={errors.preferred_channel}>
            <div className="grid grid-cols-2 gap-2">
              {[["whatsapp", "WhatsApp"], ["email", "Email"]].map(([v, l]) => (
                <button type="button" key={v} onClick={() => set("preferred_channel")(v)} data-testid={`reg-channel-${v}`}
                  className={`h-11 rounded-xl text-sm font-medium transition-colors ${form.preferred_channel === v ? "bg-[#FFF4EA] border-2 border-[#FF7A00] text-stone-900" : "border border-stone-200 text-stone-600 hover:border-[#FF7A00]/50"}`}>{l}</button>
              ))}
            </div>
          </Field>
        </Card>

        <Card className="rounded-2xl border-stone-200 p-6 space-y-4" data-testid="programme-summary">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-stone-900">{info?.name || "12-Week Cardiometabolic Nutrition Programme"}</p>
              <p className="text-xs text-stone-500 mt-0.5">{info?.duration_weeks || 12} weeks of dietitian-led care, personalised meal plans and weekly check-ins</p>
            </div>
            <p className="text-lg font-bold text-stone-900 whitespace-nowrap" data-testid="programme-price">{price}</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.programme_confirmed} onChange={set("programme_confirmed")} className="mt-1 accent-[#FF7A00]" data-testid="reg-programme-confirm" />
            <span className="text-sm text-stone-700">I confirm I'm purchasing the {info?.duration_weeks || 12}-week programme{price ? ` for ${price}` : ""}.</span>
          </label>
          {errors.programme_confirmed && <p className="text-xs text-red-600">{errors.programme_confirmed}</p>}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.privacy_consent} onChange={set("privacy_consent")} className="mt-1 accent-[#FF7A00]" data-testid="reg-consent" />
            <span className="text-sm text-stone-700">I have read the <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#FF7A00] underline">privacy notice</a> and <a href="/terms" target="_blank" rel="noreferrer" className="text-[#FF7A00] underline">terms</a>, and agree to my information being used for my care and programme communication.</span>
          </label>
          {errors.privacy_consent && <p className="text-xs text-red-600">{errors.privacy_consent}</p>}
        </Card>

        {serverError && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3" data-testid="reg-server-error">{serverError}</p>}
        <Button type="submit" disabled={submitting} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-full h-12 text-base font-bold" data-testid="reg-submit">
          {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ShieldCheck className="w-5 h-5 mr-2" />}Continue to secure payment
        </Button>
        <p className="text-xs text-stone-400 text-center">You'll be taken to Paystack to pay. PlateMind never sees or stores your card details.</p>
      </form>
    </Shell>
  );
}
